import { NavLink, useNavigate } from 'react-router-dom'
import {
  Home, Phone, BarChart3, User, Users, FileText,
  Globe, CreditCard, Bot, ClipboardList, UserRound,
  ChevronLeft, ChevronRight, Sun, Moon, LogOut, Sparkles,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useTemaStore } from '../stores/temaStore'
import { useSidebarStore } from '../stores/sidebarStore'
import { useContatoreMieiLead } from '../hooks/useMieiLead'
import { useAbbonamento } from '../hooks/useAbbonamento'
import { useNotifiche } from '../hooks/useNotifiche'
import type { Ruolo } from '../lib/tipi'
import styles from './Sidebar.module.css'

interface Voce {
  path: string
  etichetta: string
  Icona: typeof Home
  ruoli: Ruolo[]
}

/** Voci sidebar per gruppo + ruoli ammessi */
const voci: { gruppo: string; voci: Voce[] }[] = [
  {
    gruppo: 'sdr',
    voci: [
      { path: '/app/dashboard',  etichetta: 'Dashboard',    Icona: Home, ruoli: ['sdr', 'manager', 'superadmin'] },
      { path: '/app/leads',      etichetta: 'Allenati',     Icona: Phone, ruoli: ['sdr', 'manager', 'superadmin'] },
      { path: '/app/miei-lead',  etichetta: 'I miei Lead',  Icona: UserRound, ruoli: ['sdr', 'manager', 'superadmin'] },
      { path: '/app/sessioni',   etichetta: 'Sessioni',     Icona: BarChart3, ruoli: ['sdr', 'manager', 'superadmin'] },
      { path: '/app/profilo',    etichetta: 'Profilo',      Icona: User, ruoli: ['sdr', 'manager', 'superadmin'] },
      { path: '/app/abbonamento', etichetta: 'Abbonamento', Icona: CreditCard, ruoli: ['sdr', 'manager', 'superadmin'] },
    ],
  },
  {
    gruppo: 'manager',
    voci: [
      { path: '/manager/dashboard', etichetta: 'Team',     Icona: Users, ruoli: ['manager', 'superadmin'] },
      { path: '/manager/team',      etichetta: 'Membri',   Icona: Users, ruoli: ['manager', 'superadmin'] },
      { path: '/manager/sessioni',  etichetta: 'Sessioni team', Icona: BarChart3, ruoli: ['manager', 'superadmin'] },
      { path: '/manager/leads',     etichetta: 'Lead team', Icona: Phone, ruoli: ['manager', 'superadmin'] },
      { path: '/manager/report',    etichetta: 'Report',   Icona: FileText, ruoli: ['manager', 'superadmin'] },
    ],
  },
  {
    gruppo: 'superadmin',
    voci: [
      { path: '/admin/dashboard', etichetta: 'Globale',    Icona: Home, ruoli: ['superadmin'] },
      { path: '/admin/utenti',    etichetta: 'Utenti',     Icona: Globe, ruoli: ['superadmin'] },
      { path: '/admin/teams',     etichetta: 'Teams',      Icona: Users, ruoli: ['superadmin'] },
      { path: '/admin/leads',     etichetta: 'Lead di Sistema', Icona: UserRound, ruoli: ['superadmin'] },
      { path: '/admin/revenue',   etichetta: 'Revenue',    Icona: CreditCard, ruoli: ['superadmin'] },
      { path: '/admin/costi-ai',  etichetta: 'Costi AI',   Icona: Bot, ruoli: ['superadmin'] },
      { path: '/admin/audit-log', etichetta: 'Audit log',  Icona: ClipboardList, ruoli: ['superadmin'] },
    ],
  },
]

export function Sidebar() {
  const navigate = useNavigate()
  const { utente, esci } = useAuthStore()
  const { tema, imposta } = useTemaStore()
  const { collassata, toggleCollassata, drawerAperto, chiudiDrawer } = useSidebarStore()
  // Badge "X/10" sul menu "I miei Lead" — solo visibile per piano Pro
  const contatoreMieiLead = useContatoreMieiLead()
  // Stato abbonamento per badge "TRIAL Ngg" e banner upgrade
  const { stato: abb } = useAbbonamento()
  // Numero feedback pronti ma non ancora visti → badge rosso su "Sessioni"
  const { feedbackNonLetti } = useNotifiche()

  if (!utente) return null

  /** Etichetta badge da renderizzare accanto a una voce di menu, se applicabile. */
  function badgePer(path: string): { testo: string; tono: 'success' | 'warning' | 'danger' } | null {
    // Badge rosso "N" su Sessioni quando ci sono feedback pronti non visti
    if (path === '/app/sessioni' && feedbackNonLetti > 0) {
      return { testo: String(feedbackNonLetti), tono: 'danger' }
    }
    // Badge "X/10" su I miei Lead per il piano Pro
    if (path === '/app/miei-lead' && contatoreMieiLead.data) {
      const { utilizzati, limite } = contatoreMieiLead.data
      if (limite == null) return null
      const tono = utilizzati >= limite ? 'danger'
        : utilizzati >= Math.ceil(limite * 0.7) ? 'warning'
        : 'success'
      return { testo: `${utilizzati}/${limite}`, tono }
    }
    // Badge "TRIAL Ngg" su Abbonamento durante il trial
    if (path === '/app/abbonamento' && abb?.stato === 'trial') {
      const gg = abb.giorni_trial_rimanenti ?? 0
      return { testo: `${gg}gg`, tono: gg <= 2 ? 'danger' : 'warning' }
    }
    return null
  }

  const onLogout = () => {
    esci()
    navigate('/accedi')
  }

  return (
    <>
      {drawerAperto && <div className={styles.overlay} onClick={chiudiDrawer} />}
      <aside
        className={[
          styles.sidebar,
          collassata ? styles.collassata : '',
          drawerAperto ? styles.drawerAperto : '',
        ].filter(Boolean).join(' ')}
      >
        <div className={styles.header}>
          <div className={styles.logo}>
            <div className={styles.logoBox}>RC</div>
            {!collassata && (
              <span className={styles.logoTesto}>
                Ring<strong>Coach</strong>
              </span>
            )}
          </div>
        </div>

        <nav className={styles.nav}>
          {voci.map((g) => {
            const visibili = g.voci.filter((v) => v.ruoli.includes(utente.ruolo))
            if (visibili.length === 0) return null
            return (
              <div key={g.gruppo} className={styles.gruppo}>
                {!collassata && <div className={styles.gruppoTitolo}>{etichettaGruppo(g.gruppo)}</div>}
                {visibili.map((v) => {
                  const badge = badgePer(v.path)
                  return (
                    <NavLink
                      key={v.path}
                      to={v.path}
                      onClick={chiudiDrawer}
                      className={({ isActive }) =>
                        [styles.voce, isActive ? styles.attiva : ''].filter(Boolean).join(' ')
                      }
                      title={collassata ? v.etichetta : undefined}
                    >
                      <v.Icona size={18} />
                      {!collassata && <span style={{ flex: 1 }}>{v.etichetta}</span>}
                      {!collassata && badge && (
                        <span className={`${styles.badge} ${styles['badge_' + badge.tono]}`}>
                          {badge.testo}
                        </span>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* Banner "Passa a Pro" — solo per piano free, non collassata */}
        {!collassata && utente.piano === 'free' && abb?.stato === 'nessuno' && (
          <NavLink
            to="/app/abbonamento"
            onClick={chiudiDrawer}
            className={styles.bannerUpgrade}
          >
            <Sparkles size={16} />
            <div>
              <strong>Passa a Pro</strong>
              <div className={styles.bannerSub}>7 giorni gratis →</div>
            </div>
          </NavLink>
        )}

        <div className={styles.footer}>
          {!collassata && (
            <div className={styles.temaWrap}>
              <button
                className={`${styles.temaBtn} ${tema === 'light' ? styles.temaAttivo : ''}`}
                onClick={() => imposta('light')}
              >
                <Sun size={14} /> Chiaro
              </button>
              <button
                className={`${styles.temaBtn} ${tema === 'dark' ? styles.temaAttivo : ''}`}
                onClick={() => imposta('dark')}
              >
                <Moon size={14} /> Scuro
              </button>
            </div>
          )}

          <button className={styles.logout} onClick={onLogout} title={collassata ? 'Esci' : undefined}>
            <LogOut size={18} />
            {!collassata && <span>Esci</span>}
          </button>

          <button
            className={styles.collapse}
            onClick={toggleCollassata}
            aria-label={collassata ? 'Espandi' : 'Collassa'}
          >
            {collassata ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>
    </>
  )
}

function etichettaGruppo(g: string): string {
  if (g === 'sdr') return 'Allenamento'
  if (g === 'manager') return 'Manager'
  if (g === 'superadmin') return 'Superadmin'
  return g
}
